import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Bundle & save tier selector.
 *
 * Two modes, chosen per tile:
 *
 * - Variant mode (tile has a matching variant): the tile represents a real
 *   product variant with its own price. Selecting it checks the matching radio
 *   in the page's hidden native variant-picker and dispatches a real `change`
 *   event, so the theme's own variant logic runs. Quantity is forced to 1.
 *
 * - Quantity mode (no matching variant): the tile sets the cart quantity to its
 *   own quantity. Automatic discounts in Shopify Admin set the bundle price.
 *
 * In either mode a tile can list free gifts. After the theme's product form
 * adds the main product, the gifts of the selected tile are added to the cart
 * (skipping any already there) and the cart is refreshed. Buy X Get Y automatic
 * discounts in Shopify Admin make them free.
 *
 * Exception: a variant-mode tile with "Gifts ship inside the matched variant"
 * adds no gift items (the variant is mapped to include them in DSers); the main
 * product gets an "Includes" line item property instead.
 *
 * @extends {Component}
 */
export class BundleTiersComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    // window capture runs before the theme's own (document-level) submit handling
    window.addEventListener('submit', this.#onSubmitCapture, true);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    requestAnimationFrame(() => this.#applyQuantity());
    customElements.whenDefined('variant-picker').then(() => requestAnimationFrame(() => this.#syncInitialTier()));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('submit', this.#onSubmitCapture, true);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * @param {{ variantId?: string | number, quantity?: string | number }} data
   */
  selectTier(data) {
    const section = this.#section;
    const variantId = data?.variantId;

    if (variantId) {
      const variantPicker = section?.querySelector('variant-picker');
      const radio = variantPicker?.querySelector(`[data-variant-id="${variantId}"]`);

      if (radio instanceof HTMLInputElement && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.#applyQuantity();
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  /**
   * Variant mode only. On load the page shows Shopify's default variant, which
   * may not be the tile marked as selected. If the URL names a variant that has
   * a tile, select that tile; otherwise apply the default tile's variant.
   */
  #syncInitialTier() {
    /** @type {HTMLInputElement[]} */
    const radios = Array.from(this.querySelectorAll('.bundle-tier__radio'));
    const variantIdOf = (/** @type {HTMLInputElement} */ radio) =>
      (radio.getAttribute('on:change') || '').match(/variantId=(\d+)/)?.[1];
    if (!radios.some(variantIdOf)) return;

    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    const urlRadio = urlVariant ? radios.find((radio) => variantIdOf(radio) === urlVariant) : undefined;
    if (urlRadio) {
      urlRadio.checked = true;
      return;
    }

    const checked = this.#checkedTier;
    const variantId = checked && variantIdOf(checked);
    if (variantId) this.selectTier({ variantId });
  }

  /** @returns {HTMLInputElement | null} */
  get #checkedTier() {
    return this.querySelector('.bundle-tier__radio:checked');
  }

  /** @returns {number} */
  get #selectedQuantity() {
    const tier = this.#checkedTier;
    if (!tier) return 1;

    // In variant mode each variant is already a full pack, so quantity stays 1.
    const onChange = tier.getAttribute('on:change') || '';
    if (/variantId=\d+/.test(onChange)) return 1;

    return Number(tier.dataset.quantity) || 1;
  }

  #applyQuantity() {
    const productForm = this.#section?.querySelector('product-form-component');
    const quantitySelector = /** @type {any} */ (productForm)?.refs?.quantitySelector;

    if (quantitySelector?.setValue) {
      quantitySelector.setValue(String(this.#selectedQuantity));
      quantitySelector.updateButtonStates?.();
    }
  }

  /**
   * Re-applies the tier quantity just before the product form reads it,
   * in case the quantity selector was re-rendered after the tile was chosen.
   * @param {Event} event
   */
  #onSubmitCapture = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!this.#section?.contains(form)) return;

    this.#applyQuantity();
    this.#applyIncludes(form);
  };

  /**
   * When a tile's gifts ship inside its variant, nothing extra is added to the
   * cart, so attach an "Includes" line item property to the main product instead.
   * Written at submit time so variant re-renders can't leave a stale value.
   * @param {HTMLFormElement} form
   */
  #applyIncludes(form) {
    const includes = this.#checkedTier?.dataset.includes || '';
    let input = form.querySelector('input[data-bundle-includes]');

    if (!includes) {
      input?.remove();
      return;
    }

    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Includes]';
      input.setAttribute('data-bundle-includes', '');
      form.append(input);
    }
    /** @type {HTMLInputElement} */ (input).value = includes;
  }

  /**
   * After the product form adds the main product, add the selected tile's gifts.
   * @param {Event} event
   */
  #onCartUpdate = async (event) => {
    const detail = /** @type {any} */ (event).detail;
    const data = detail?.data || {};

    if (data.source !== 'product-form-component' || data.didError) return;

    const productForm = this.#section?.querySelector('product-form-component');
    if (!(productForm instanceof HTMLElement)) return;
    if (String(data.productId) !== String(productForm.dataset.productId)) return;

    const giftIds = (this.#checkedTier?.dataset.giftIds || '')
      .split(',')
      .map((id) => Number(id))
      .filter(Boolean);
    if (giftIds.length === 0) return;

    const cartItems = Array.isArray(detail?.resource?.items) ? detail.resource.items : await this.#fetchCartItems();
    const inCart = new Set(cartItems.map((/** @type {any} */ item) => Number(item.variant_id)));
    const toAdd = giftIds.filter((id) => !inCart.has(id));
    if (toAdd.length === 0) return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: toAdd.map((id) => ({ id, quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      }).then((res) => res.json());

      if (response.status) {
        console.error('Bundle gifts could not be added:', response.message);
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = cartResponse.ok ? await cartResponse.json() : undefined;

      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id || 'bundle-tiers', {
          source: 'bundle-gifts',
          itemCount: cart?.item_count,
          sections: response.sections,
        })
      );
    } catch (error) {
      console.error('Bundle gifts could not be added:', error);
    }
  };

  /** @returns {Promise<Array<any>>} */
  async #fetchCartItems() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return [];
      const cart = await response.json();
      return Array.isArray(cart.items) ? cart.items : [];
    } catch {
      return [];
    }
  }
}

if (!customElements.get('bundle-tiers-component')) {
  customElements.define('bundle-tiers-component', BundleTiersComponent);
}
